/**
 * Pure helpers for the Program Comparison feature.
 * Extracted from ProgramComparisonDialog.jsx to keep the dialog focused on UI.
 */
import { tallyWorkout } from "../muscleVolume";

// Distinct, color-blind-friendly palette for up to 8 programs.
export const SERIES_COLORS = [
  "#007AFF", // blue
  "#D4FF00", // lime (brand accent)
  "#FF9500", // orange
  "#AF52DE", // purple
  "#34C759", // green
  "#FF3B30", // red
  "#5AC8FA", // cyan
  "#FFD60A", // yellow
];

export const colorFor = (idx) => SERIES_COLORS[idx % SERIES_COLORS.length];

/** Parse a reps string like "10", "8-12", "8/10/12", "AMRAP", "max" into a
 *  representative number (mid value for ranges, average for slash-lists,
 *  0 if it can't be parsed). */
export function parseReps(raw) {
  if (raw === null || raw === undefined) return 0;
  const s = String(raw).trim();
  if (!s) return 0;
  const range = s.match(/^\s*(\d+)\s*[-–]\s*(\d+)\s*$/);
  if (range) {
    return (parseFloat(range[1]) + parseFloat(range[2])) / 2;
  }
  if (s.includes("/")) {
    const parts = s.split("/").map((p) => parseFloat(p)).filter((n) => !Number.isNaN(n));
    if (parts.length) return parts.reduce((a, b) => a + b, 0) / parts.length;
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

/** Aggregate metrics for a single program: totals + muscle-group tally. */
export function summarizeProgram(program, workouts, libMap, tallyOpts) {
  const programWorkouts = workouts.filter((w) => w.program_id === program.id);
  let totalSets = 0;
  let totalReps = 0;
  let exerciseCount = 0;
  const tally = {};
  programWorkouts.forEach((w) => {
    (w.exercises || []).forEach((ex) => {
      const sets = parseInt(ex.sets, 10) || 0;
      totalSets += sets;
      totalReps += sets * parseReps(ex.reps);
      exerciseCount += 1;
    });
    const { tally: t } = tallyWorkout(w.exercises || [], libMap, tallyOpts);
    Object.entries(t).forEach(([g, v]) => {
      tally[g] = (tally[g] || 0) + v;
    });
  });
  return {
    id: program.id,
    title: program.title,
    workoutsCount: programWorkouts.length,
    exerciseCount,
    totalSets,
    totalReps: Math.round(totalReps),
    tally,
  };
}

/** Build the chart rows: one row per muscle-group, one column per program. */
export function buildChartData(summaries, groupsAll) {
  if (summaries.length === 0) return [];
  const groupKeys = new Set();
  summaries.forEach((s) => Object.keys(s.tally).forEach((k) => groupKeys.add(k)));
  const ordered = groupsAll.filter((g) => groupKeys.has(g.key));
  return ordered.map((g) => {
    const row = { muscle: g.label };
    summaries.forEach((s) => {
      row[s.id] = Math.round((s.tally[g.key] || 0) * 10) / 10;
    });
    return row;
  });
}
